<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  hasTemplateVariables,
  hasTemplateMediaHeader,
} from 'dashboard/helper/templateHelper';

import Button from 'dashboard/components-next/button/Button.vue';
import StepTemplateSelect from './wizard/StepTemplateSelect.vue';
import StepAudienceSelect from './wizard/StepAudienceSelect.vue';
import StepVariableConfig from './wizard/StepVariableConfig.vue';
import StepCsvUpload from './wizard/StepCsvUpload.vue';
import StepColumnMapping from './wizard/StepColumnMapping.vue';
import StepPreview from './wizard/StepPreview.vue';
import StepDispatchSpeed from './wizard/StepDispatchSpeed.vue';
import StepConfirm from './wizard/StepConfirm.vue';

defineProps({
  isSubmitting: { type: Boolean, default: false },
});

const emit = defineEmits(['submit', 'cancel', 'dirtyChange']);

const { t } = useI18n();

const templateSelectRef = ref(null);
const audienceSelectRef = ref(null);
const csvUploadRef = ref(null);
const variableConfigRef = ref(null);
const columnMappingRef = ref(null);
const currentStep = ref(0);
const wizardData = ref({
  title: '',
  inboxId: null,
  templateId: null,
  selectedTemplate: null,
  audienceType: 'label',
  selectedAudience: [],
  excludedLabels: [],
  conversationLabelId: null,
  importId: null,
  csvHeaders: [],
  csvSampleRows: [],
  csvTotalRows: 0,
  autoLabel: null,
  columnMapping: {},
  templateParams: {},
  processedParams: {},
  renderedTemplate: '',
  throttleConfig: { profile: 'moderate' },
  scheduledAt: null,
});

const isLabelAudience = computed(
  () => wizardData.value.audienceType === 'label'
);

// Tracks whether the user has made any meaningful progress, so the parent
// can confirm before discarding a partially filled wizard.
const isDirty = computed(
  () =>
    currentStep.value > 0 ||
    wizardData.value.title !== '' ||
    wizardData.value.templateId !== null ||
    wizardData.value.importId !== null ||
    wizardData.value.selectedAudience.length > 0
);

watch(isDirty, value => emit('dirtyChange', value));

const templateNeedsConfig = computed(
  () =>
    hasTemplateVariables(wizardData.value.selectedTemplate) ||
    hasTemplateMediaHeader(wizardData.value.selectedTemplate)
);

const steps = computed(() => {
  const base = [
    {
      key: 'template',
      label: t('CAMPAIGN.WHATSAPP.WIZARD.STEPS.TEMPLATE'),
    },
    {
      key: 'audience',
      label: t('CAMPAIGN.WHATSAPP.WIZARD.STEPS.AUDIENCE'),
    },
  ];

  if (isLabelAudience.value) {
    if (templateNeedsConfig.value) {
      base.push({
        key: 'variable-config',
        label: t('CAMPAIGN.WHATSAPP.WIZARD.STEPS.VARIABLE_CONFIG'),
      });
    }
  } else {
    base.push(
      {
        key: 'csv-upload',
        label: t('CAMPAIGN.WHATSAPP.WIZARD.STEPS.CSV_UPLOAD'),
      },
      {
        key: 'column-mapping',
        label: t('CAMPAIGN.WHATSAPP.WIZARD.STEPS.COLUMN_MAPPING'),
      }
    );
  }

  base.push(
    { key: 'preview', label: t('CAMPAIGN.WHATSAPP.WIZARD.STEPS.PREVIEW') },
    {
      key: 'dispatch-speed',
      label: t('CAMPAIGN.WHATSAPP.WIZARD.STEPS.DISPATCH_SPEED'),
    },
    { key: 'confirm', label: t('CAMPAIGN.WHATSAPP.WIZARD.STEPS.CONFIRM') }
  );

  return base;
});

const currentStepKey = computed(() => steps.value[currentStep.value]?.key);
const isFirstStep = computed(() => currentStep.value === 0);
const isLastStep = computed(() => currentStep.value === steps.value.length - 1);

const stepRefs = computed(() => ({
  template: templateSelectRef,
  audience: audienceSelectRef,
  'csv-upload': csvUploadRef,
  'variable-config': variableConfigRef,
  'column-mapping': columnMappingRef,
}));

const goNext = () => {
  if (isLastStep.value) return;

  const stepRef = stepRefs.value[currentStepKey.value];
  if (stepRef?.value?.validate && !stepRef.value.validate()) return;

  currentStep.value += 1;
};

const goBack = () => {
  if (!isFirstStep.value) {
    currentStep.value -= 1;
  }
};

const handleStepUpdate = data => {
  Object.assign(wizardData.value, data);
};

const handleSubmit = () => {
  emit('submit', wizardData.value);
};

const handleCancel = () => {
  emit('cancel');
};

const reset = () => {
  currentStep.value = 0;
  wizardData.value = {
    title: '',
    inboxId: null,
    templateId: null,
    selectedTemplate: null,
    audienceType: 'label',
    selectedAudience: [],
    excludedLabels: [],
    conversationLabelId: null,
    importId: null,
    csvHeaders: [],
    csvSampleRows: [],
    csvTotalRows: 0,
    autoLabel: null,
    columnMapping: {},
    templateParams: {},
    processedParams: {},
    renderedTemplate: '',
    throttleConfig: { profile: 'moderate' },
    scheduledAt: null,
  };
};

defineExpose({ reset });
</script>

<template>
  <div class="flex flex-col gap-6">
    <!-- Stepper -->
    <div class="flex items-center gap-2">
      <template v-for="(step, index) in steps" :key="step.key">
        <div
          class="flex items-center gap-2"
          :class="{ 'opacity-50': index > currentStep }"
        >
          <div
            class="flex items-center justify-center rounded-full size-7 text-xs font-medium"
            :class="
              index <= currentStep
                ? 'bg-n-brand text-white'
                : 'bg-n-alpha-2 text-n-slate-11'
            "
          >
            <span v-if="index < currentStep" class="i-lucide-check size-3.5" />
            <span v-else>{{ index + 1 }}</span>
          </div>
          <span
            class="text-xs font-medium hidden sm:inline"
            :class="
              index <= currentStep ? 'text-n-slate-12' : 'text-n-slate-10'
            "
          >
            {{ step.label }}
          </span>
        </div>
        <div
          v-if="index < steps.length - 1"
          class="flex-1 h-px bg-n-alpha-3 min-w-4"
        />
      </template>
    </div>

    <!-- Step Content -->
    <div class="min-h-[450px]">
      <StepTemplateSelect
        v-if="currentStepKey === 'template'"
        ref="templateSelectRef"
        :wizard-data="wizardData"
        @update="handleStepUpdate"
      />
      <StepAudienceSelect
        v-else-if="currentStepKey === 'audience'"
        ref="audienceSelectRef"
        :wizard-data="wizardData"
        @update="handleStepUpdate"
        @next="currentStep += 1"
      />
      <StepVariableConfig
        v-else-if="currentStepKey === 'variable-config'"
        ref="variableConfigRef"
        :wizard-data="wizardData"
        @update="handleStepUpdate"
      />
      <StepCsvUpload
        v-else-if="currentStepKey === 'csv-upload'"
        ref="csvUploadRef"
        :wizard-data="wizardData"
        @update="handleStepUpdate"
      />
      <StepColumnMapping
        v-else-if="currentStepKey === 'column-mapping'"
        ref="columnMappingRef"
        :wizard-data="wizardData"
        @update="handleStepUpdate"
      />
      <StepPreview
        v-else-if="currentStepKey === 'preview'"
        :wizard-data="wizardData"
      />
      <StepDispatchSpeed
        v-else-if="currentStepKey === 'dispatch-speed'"
        :wizard-data="wizardData"
        @update="handleStepUpdate"
      />
      <StepConfirm
        v-else-if="currentStepKey === 'confirm'"
        :wizard-data="wizardData"
        @update="handleStepUpdate"
      />
    </div>

    <!-- Navigation -->
    <div class="flex items-center justify-between pt-4 border-t border-n-weak">
      <div class="flex items-center gap-2">
        <Button
          v-if="!isFirstStep"
          :label="t('CAMPAIGN.WHATSAPP.WIZARD.BUTTONS.BACK')"
          variant="link"
          icon="i-lucide-arrow-left"
          class="h-10 hover:!no-underline hover:text-n-brand"
          @click="goBack"
        />
        <Button
          :label="t('CAMPAIGN.WHATSAPP.WIZARD.BUTTONS.CANCEL')"
          variant="link"
          color="slate"
          class="h-10 hover:!no-underline hover:text-n-brand"
          @click="handleCancel"
        />
      </div>

      <Button
        v-if="!isLastStep"
        :label="t('CAMPAIGN.WHATSAPP.WIZARD.BUTTONS.NEXT')"
        color="blue"
        @click="goNext"
      />
      <Button
        v-else
        :label="t('CAMPAIGN.WHATSAPP.WIZARD.BUTTONS.CREATE_CAMPAIGN')"
        color="blue"
        :disabled="isSubmitting"
        :is-loading="isSubmitting"
        @click="handleSubmit"
      />
    </div>
  </div>
</template>
